import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import React, { useState } from 'react';
import { EmbeddingsLink } from '../../../components/EmbeddingsLink';
import { EmbeddingsList } from '../../../components/EmbeddingsList';
import { ImageZoomModal } from '../../embeddings/components/ImageZoomModal';

interface PogProductContentProps {
  item: any; // POG box data
  chainId?: number;
  storeId?: number;
  storeName?: string;
  shouldShowEmbeddings?: boolean;
}

export const PogProductContent: React.FC<PogProductContentProps> = ({
  item,
  chainId,
  storeId,
  storeName,
  shouldShowEmbeddings = false,
}) => {
  const [showZoomedImage, setShowZoomedImage] = useState(false);
  return (
    <div className="p-6">
      {/* Hero Section with Product Image */}
      <div className="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-lg p-6 mb-6">
        <div className="flex items-center gap-6">
          {/* Product Image */}
          <div className="flex-shrink-0">
            {item.imageUri ? (
              <div
                className="w-32 h-32 bg-white rounded-lg shadow-md p-3 flex items-center justify-center cursor-pointer hover:shadow-lg transition-shadow"
                onClick={() => setShowZoomedImage(true)}
              >
                <img
                  src={item.imageUri}
                  alt={item.name || 'Product'}
                  className="max-w-full max-h-full object-contain hover:opacity-90 transition-opacity"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    target.nextElementSibling?.classList.remove('hidden');
                  }}
                />
                <div className="hidden w-full h-full bg-gray-100 rounded flex items-center justify-center">
                  <svg
                    className="w-12 h-12 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={1.5}
                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              </div>
            ) : (
              <div className="w-32 h-32 bg-white rounded-lg shadow-md flex items-center justify-center">
                <svg
                  className="w-12 h-12 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1.5}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
              </div>
            )}
          </div>

          {/* Product Details */}
          <div className="flex-1 min-w-0">
            <h3 className="text-2xl font-bold text-gray-900 mb-2 leading-tight flex items-center gap-2 flex-wrap">
              <span>{item.name || 'Unknown Product'}</span>
              {item.isActive === false && (
                <span className="inline-flex items-center gap-1 text-orange-500 text-base font-semibold">
                  <WarningAmberIcon fontSize="small" />
                  Inactive
                </span>
              )}
            </h3>

            <div className="flex items-center gap-4 mb-3">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-600">UPC:</span>
                <span className="text-lg font-mono font-semibold text-blue-600 bg-white px-3 py-1 rounded-md shadow-sm">
                  {item.upc || 'N/A'}
                </span>
              </div>

              {item.productId && (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">Product ID:</span>
                  <span className="text-sm font-mono font-semibold text-gray-700 bg-white px-2 py-1 rounded-md shadow-sm">
                    {item.productId}
                  </span>
                </div>
              )}

              {item.itemNumber && (
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-600">Item Number:</span>
                  <span className="text-sm font-mono font-semibold text-gray-700 bg-white px-2 py-1 rounded-md shadow-sm">
                    {item.itemNumber}
                  </span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <svg
                  className="w-5 h-5 text-blue-500"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <span className="text-sm font-medium text-blue-600">
                  POG Position: {item.tagName || 'Unknown'}
                </span>
              </div>

              {!shouldShowEmbeddings && item.upc && chainId && (
                <EmbeddingsLink
                  upc={item.upc}
                  chainId={chainId}
                  storeId={storeId}
                  storeName={storeName}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Technical Details */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-lg font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <svg
            className="w-5 h-5 text-gray-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
          Bounding Box Details
        </h4>

        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-600">X1 (Left):</span>
              <span className="text-sm font-mono text-gray-900">{item.x1}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-600">Y1 (Top):</span>
              <span className="text-sm font-mono text-gray-900">{item.y1}</span>
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-600">X2 (Right):</span>
              <span className="text-sm font-mono text-gray-900">{item.x2}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-600">Y2 (Bottom):</span>
              <span className="text-sm font-mono text-gray-900">{item.y2}</span>
            </div>
          </div>
        </div>

        <div className="mt-3 pt-3 border-t border-gray-200">
          <div className="grid grid-cols-2 gap-4">
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-600">Width:</span>
              <span className="text-sm font-mono text-gray-900">{item.x2 - item.x1} px</span>
            </div>
            <div className="flex justify-between">
              <span className="text-sm font-medium text-gray-600">Height:</span>
              <span className="text-sm font-mono text-gray-900">{item.y2 - item.y1} px</span>
            </div>
          </div>
        </div>
      </div>

      {/* Embeddings List */}
      {shouldShowEmbeddings && item.upc && chainId && (
        <div className="mt-6">
          <EmbeddingsList
            normalizedBarcode={item.upc}
            chainId={chainId}
            storeId={storeId}
            storeName={storeName}
            itemNumber={item.itemNumber}
          />
        </div>
      )}

      {/* Zoom Modal */}
      <ImageZoomModal
        isOpen={showZoomedImage && !!item.imageUri}
        imageSrc={item.imageUri || ''}
        onClose={() => setShowZoomedImage(false)}
      />
    </div>
  );
};
